import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star, Users, Award, TrendingUp, Zap, Shield, Clock, DollarSign, RefreshCw, ChevronDown } from 'lucide-react';
import { OptimizedImage } from '../components/OptimizedImage';

export const Home: React.FC = () => {
  const features = [
    {
      icon: <Zap className="h-8 w-8 text-accent-500" aria-hidden="true" />,
      title: "Lightning Fast",
      description: "Optimized for speed and performance across all devices"
    },
    {
      icon: <Shield className="h-8 w-8 text-accent-500" aria-hidden="true" />,
      title: "Secure & Reliable",
      description: "Enterprise-grade security with 99.9% uptime guarantee"
    },
    {
      icon: <Users className="h-8 w-8 text-accent-500" aria-hidden="true" />,
      title: "Expert Team",
      description: "Dedicated professionals with years of experience"
    },
    {
      icon: <DollarSign className="h-8 w-8 text-accent-500" aria-hidden="true" />,
      title: "$1 Down",
      description: "Low Monthly Recurring, starting at $199"
    },
    {
      icon: <RefreshCw className="h-8 w-8 text-accent-500" aria-hidden="true" />,
      title: "Unlimited Changes",
      description: "No hidden Fees & no additional cost"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      company: "TechStart Inc.",
      image: "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
      text: "OutstandingNet transformed our online presence completely. The new website not only looks amazing but has increased our conversions by 300%."
    },
    {
      name: "Michael Chen",
      company: "Green Solutions",
      image: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
      text: "The team's attention to detail and professional approach made the entire process smooth. Our website now truly represents our brand."
    },
    {
      name: "Emily Rodriguez",
      company: "Creative Agency",
      image: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop",
      text: "Outstanding service from start to finish. They understood our vision and delivered beyond our expectations."
    }
  ];

  const stats = [
    { number: "500+", label: "Websites Built" },
    { number: "98%", label: "Client Satisfaction" },
    { number: "15+", label: "Years Experience" },
    { number: "$1", label: "Starting Price" }
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 hero-gradient">
          {/* Animated gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-600/80 via-secondary-600/70 to-accent-600/60 animate-gradient-shift"></div>
          
          {/* Floating geometric shapes - reduced for better performance */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full animate-float-slow"></div>
            <div className="absolute bottom-40 left-20 w-12 h-12 bg-secondary-400/20 rounded-full animate-float-fast" style={{animationDelay: '4s'}}></div>
            
            {/* Larger floating elements - reduced for better performance */}
            <div className="absolute top-1/4 left-1/2 w-32 h-32 bg-gradient-to-br from-white/5 to-accent-400/10 rounded-full animate-pulse-slow"></div>
          </div>
          
          {/* Simplified animated mesh gradient */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent animate-mesh-1"></div>
          </div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in-up">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6">
              Transform Your
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-secondary-400 animate-text-shimmer pb-4">
                Digital Presence
              </span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              We create outstanding websites that drive results. From design to deployment, 
              we're your trusted partner in building exceptional web experiences.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up" style={{animationDelay: '0.4s'}}>
              <Link to="/get-started" className="btn-primary text-lg px-8 py-4 transform hover:scale-105 transition-all duration-300">
                Start Your Project
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Link>
              <Link to="/portfolio" className="btn-secondary text-lg px-8 py-4 transform hover:scale-105 transition-all duration-300">
                View Our Work
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Down Indicator - Positioned at bottom center, 20px from bottom */}
        <div className="absolute bottom-5 left-0 right-0 flex flex-col items-center animate-fade-in-up" style={{animationDelay: '0.8s'}}>
          <p className="text-white/80 text-sm mb-2">Scroll Down</p>
          <ChevronDown className="h-6 w-6 text-white/80 animate-bounce" aria-hidden="true" />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Choose OutstandingNet?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine creativity, technology, and strategy to deliver websites that not only look great but perform exceptionally.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="card p-8 text-center group">
                <div className="mb-6 flex justify-center transform group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From initial concept to ongoing maintenance, we provide comprehensive web solutions tailored to your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card p-8 group">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Web Design</h3>
              <p className="text-gray-600 mb-6">
                Custom, responsive designs that capture your brand essence and engage your audience.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-accent-500 mr-2" aria-hidden="true" />
                  <span className="text-gray-700">Responsive Design</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-accent-500 mr-2" aria-hidden="true" />
                  <span className="text-gray-700">User Experience Focus</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-accent-500 mr-2" aria-hidden="true" />
                  <span className="text-gray-700">Brand Integration</span>
                </li>
              </ul>
            </div>

            <div className="card p-8 group">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Web Development</h3>
              <p className="text-gray-600 mb-6">
                Robust, scalable websites built with the latest technologies and best practices.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-accent-500 mr-2" aria-hidden="true" />
                  <span className="text-gray-700">Modern Technologies</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-accent-500 mr-2" aria-hidden="true" />
                  <span className="text-gray-700">Performance Optimized</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-accent-500 mr-2" aria-hidden="true" />
                  <span className="text-gray-700">SEO Ready</span>
                </li>
              </ul>
            </div>

            <div className="card p-8 group">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Website Management</h3>
              <p className="text-gray-600 mb-6">
                Ongoing maintenance, updates, and optimization to keep your website running smoothly.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-accent-500 mr-2" aria-hidden="true" />
                  <span className="text-gray-700">Regular Updates</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-accent-500 mr-2" aria-hidden="true" />
                  <span className="text-gray-700">Security Monitoring</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-accent-500 mr-2" aria-hidden="true" />
                  <span className="text-gray-700">Performance Analytics</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="text-white">
                <div className="text-4xl sm:text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-xl text-primary-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don't just take our word for it. Here's what our satisfied clients have to say about working with us.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="card p-8">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" aria-hidden="true" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <OptimizedImage
                    src={testimonial.image}
                    alt={testimonial.name}
                    width={48}
                    height={48}
                    className="rounded-full mr-4"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-gray-500">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Ready to Transform Your Online Presence?
          </h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Let's discuss your project and create something outstanding together. 
            Get a free consultation and quote today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="btn-primary text-lg px-8 py-4">
              Get Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </Link>
            <Link to="/portfolio" className="btn-outline text-lg px-8 py-4">
              View Portfolio
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};